import { RpcClient } from "./RpcClient.js";
import { Logger } from "./Logger.js";
import { EventManager } from "./EventManager.js";
import { LayoutEngine } from "./LayoutEngine.js";
import { SessionTokenHandler } from "./SessionTokenHandler.js";
import { ClientControl } from "./ClientControl.js";
import { ClientCommandHandler } from "./ClientCommandHandler.js";
import { Notifier } from "./Notifier.js";
import { TranslationManager } from "./TranslationManager.js";
import { PWAPlatform } from "./PWAPlatform.js";
import { PushManager } from "./PushManager.js"; 
import { ReactiveDataStore } from "./ReactiveDataStore.js";
import { PermissionsAPI } from "./PermissionsAPI.js";
import { HotkeyManager } from "./HotkeyManager.js";
import { AppearanceManager } from "./AppearanceManager.js";

export class ApplicationClient {
  constructor(config = {}) {
    this.config = {
      rootLayoutName: null,
      rootContainer: null,
      rpcClient: {
        errorHandler: {
          // Default error handler
          default: (error) => {
            console.error("RPC Error:", error);
          },
          middleware: {
            // Specific middleware error handlers
            Login: (error) => {
              // Example: Redirect to login
              // window.location.href = "/login";
            },
            // ... other middleware error handlers
          },
        },
      },
      session: {  // Moved session config here
        enabled: false,
        authRpc: "AuthRpc"
      },

      projectName: null,
      ...config,
    };


    this.socket = null;
    this.layoutEngine = null;
    this.modules = {};
    this.logger = null;
    this.rpcClient = null;
    this.events = null;
    this.Logger = Logger;
    this.initializeSocketConnection();
    this._sharedData = {};
    this.sessionTokenHandler = null;
    this.notifier = new Notifier(this);
    this._initialized = false;
    this.clientControl = null;
    this.clientCommandHandler = null;
    this.tx = new TranslationManager(this);
    this.platform = null;
    this.pushManager = null;
    this.permissions = new PermissionsAPI({ appClient: this });
    this.rds = null;
    this.hotkeys = new HotkeyManager({ appClient: this });
    this.appearance = new AppearanceManager({ appClient: this });
  }

  initializeSocketConnection() {
    this.socket = io();

    this.socket.on("connect", () => {
      if (this.log) {
        this.log.info("Connected to server");
      } else {
        console.log("Connected to server");
      }
      if (this.events) {
        this.events.fireLocal("SERVER_CONNECTED");
      }
    });

    this.socket.on("disconnect", () => {
      if (this.log) {
        this.log.info("Disconnected from server");
      } else {
        console.log("Disconnected from server");
      }
      if (this.events) {
        this.events.fireLocal("SERVER_DISCONNECTED");
      }
    });

  }

  async start() {

    this.log = new Logger("ApplicationClient");;
    await this.hotkeys.initialize();
    await this.notifier.initialize();


    this.events = new EventManager(this.socket);

    this.events.local.on("USER_LOGGED_IN", () => {
      this.onReady(() => {
        setTimeout(() => {
          this.permissions.promptFirstTimePermissions().catch(() => {});
        }, 600);
      });
    });

    if (this.config.session?.enabled) {
      this.sessionTokenHandler = new SessionTokenHandler(
        this.config.session,
        this
      );
    }

    this.platform = new PWAPlatform({ appClient: this });
    await this.platform.initialize();

    // Intercept link clicks globally for platform-aware external link handling (web, electron, capacitor)
    document.addEventListener('click', (e) => {
      const anchor = e.target.closest('a');
      if (anchor) {
        // Skip links with download attributes
        if (anchor.hasAttribute('download')) {
          return;
        }

        const href = anchor.getAttribute('href');
        if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
          try {
            const url = new URL(anchor.href, window.location.href);
            // Only intercept standard web protocols to avoid disrupting mailto/tel/file protocols
            const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
            if (!isHttp) {
              return;
            }

            const isExternal = url.origin !== window.location.origin;
            const isOpenInNewWindow = anchor.getAttribute('target') === '_blank';
            const inMessage = anchor.closest('.ws-msg-bubble, .opc-body, .ws-system-msg, .ws-turn-artifact');

            if (isExternal || isOpenInNewWindow || inMessage) {
              e.preventDefault();

              const showConfirm = !!(this.config.showConfirmOverlay ?? this.config.options?.showConfirmOverlay);

              this.platform.openExternalUrl(anchor.href, { showConfirmOverlay: showConfirm }).catch(err => {
                this.log.error("Failed to open external URL", err);
              });
            }
          } catch (err) {
            // Ignore invalid URLs
          }
        }
      }
    });

    this.rpcClient = new RpcClient(this.socket, this, this.config.rpcClient);
    this.events.fireLocal("ALL_MODULES_INITIALIZING");

    this.pushManager = new PushManager({ appClient: this });
    await this.pushManager.initialize();


    if (this.config.session?.enabled) {
      await this.sessionTokenHandler.initialize();
    }

    await this.tx.initialize();


    this.clientControl = new ClientControl(this); // Instantiate ClientControl
    await this.clientControl.initialize(); // Initialize ClientControl

    this.clientCommandHandler = new ClientCommandHandler({ appClient: this });
    await this.clientCommandHandler.initialize();


    this.rds = new ReactiveDataStore(this); 
    await this.rds.initialize(); // Initialize RDS
    await this.rds.connect();    // Perform initial connection for 'always' data sets

    if (this.config.layoutConfig && this.config.rootContainer) {
      this.layoutEngine = new LayoutEngine(this);
      await this.layoutEngine.initialize({
        layoutConfig: this.config.layoutConfig,
        rootContainer: this.config.rootContainer,
        appClient: this,
        prefix: ""
      });
    } else {
      this.log.warn("LayoutEngine not initialized. layoutConfig or rootContainer missing.");
    }

    this._initialized = true;
    this.events.fireLocal("ALL_MODULES_INITIALIZED");
    window.uiProgressManager?.hide?.();

    // Cold boot check: if the user is already logged in, check permissions after initialization completes
    if (this.isLoggedIn()) {
      this.permissions.promptFirstTimePermissions().catch(() => {});
    }

  }

  onReady(cb) {
    if (this._initialized) {
      cb();
      return;
    }
    if (!this.events) {
      setTimeout(() => this.onReady(cb), 10);
      return;
    }
    this.events.local.on("ALL_MODULES_INITIALIZED", () => {
      cb();
      this.events.local.off("ALL_MODULES_INITIALIZED", this); //need to verify this.
    });
  }

  isLoggedIn() {
    if (!this.config.session.enabled) {
      return true;
    }
    return this.sessionTokenHandler ? this.sessionTokenHandler.getCachedLoginStatus() : false;
  }

  setSharedData(key, value) {
    this._sharedData[key] = value;
  }

  getSharedData(key) {
    return this._sharedData[key];
  }
}
